// Layer: presentation (composition root for the browser bundle).
import React from "react";
import ReactDOM from "react-dom/client";
import { HttpStoriaApi } from "./infrastructure/http-api";
import {
  beginLogin,
  completeLogin,
  readCallback,
  scrubCallback,
} from "./infrastructure/oidc";
import { InMemoryTokenSource, tokenFromLocation } from "./infrastructure/token-source";
import { App } from "./presentation/App";
import { SAMPLE_PLAYBOOKS } from "./presentation/sample-playbook";
import "./presentation/styles.css";

const oidc = {
  authorizeUrl: import.meta.env.VITE_OIDC_AUTHORIZE_URL ?? "",
  tokenUrl: import.meta.env.VITE_OIDC_TOKEN_URL ?? "",
  clientId: import.meta.env.VITE_OIDC_CLIENT_ID ?? "",
  redirectUri: import.meta.env.VITE_OIDC_REDIRECT_URI ?? window.location.origin,
  scope: import.meta.env.VITE_OIDC_SCOPE,
};
const oidcConfigured = Boolean(oidc.authorizeUrl && oidc.tokenUrl && oidc.clientId);

// Two ways in, and both end with the credential in memory only.
//
//   OIDC          the console redirects, comes back with ?code=, and redeems it with
//                 PKCE. This is the real flow.
//   fragment      a host portal hands us a token directly. Retained because some
//                 operators front the console with their own SSO proxy.
const tokens = new InMemoryTokenSource(
  tokenFromLocation(window.location, window.history),
);

async function bootstrap(): Promise<boolean> {
  if (!oidcConfigured) return (await tokens.get()) !== null;

  const callback = readCallback(window.location);
  if (callback) {
    try {
      const response = await completeLogin(oidc, callback);
      tokens.set(response.access_token);
    } catch (error) {
      console.error("sign-in failed", error);
    } finally {
      // Never leave the code in history, successful or not.
      scrubCallback(window.location, window.history);
    }
  }
  return (await tokens.get()) !== null;
}

async function signIn(): Promise<void> {
  window.location.assign(await beginLogin(oidc));
}

const api = new HttpStoriaApi({
  baseUrl: import.meta.env.VITE_API_BASE ?? "http://localhost:8000",
  tokens,
});

const propertyIds = (import.meta.env.VITE_PROPERTY_IDS ?? "")
  .split(",")
  .map((id: string) => id.trim())
  .filter(Boolean);

void bootstrap().then((signedIn) => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <App
        api={api}
        propertyId={propertyIds[0] ?? ""}
        propertyIds={propertyIds}
        samplePlaybooks={SAMPLE_PLAYBOOKS}
        operatorName={import.meta.env.VITE_OPERATOR_NAME}
        signedIn={signedIn}
        onSignIn={oidcConfigured ? () => void signIn() : undefined}
        // The same object as `api`: HttpStoriaApi implements both ports. Passed
        // separately so the shell depends on the narrow one it actually reads.
        activity={api}
      />
    </React.StrictMode>,
  );
});
